import React, { useState } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import DropzoneArea from '../DropzoneArea';

const HeshinAccountCardsImportModal = ({
    open,
    onClose,
    caseId,
    token,
    onSuccess,
    previewHeshinAccountCards,
    importHeshinAccountCards,
    t,
    direction = 'rtl',
}) => {
    const [prmFile, setPrmFile] = useState(null);
    const [datFile, setDatFile] = useState(null);
    const [reverseName, setReverseName] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handlePrmDrop = (files) => {
        if (files?.length) setPrmFile(files[0]);
        else setPrmFile(null);
        setPreviewData(null);
    };

    const handleDatDrop = (files) => {
        if (files?.length) setDatFile(files[0]);
        else setDatFile(null);
        setPreviewData(null);
    };

    const handlePreview = async () => {
        if (!prmFile || !datFile || !caseId || !token || !previewHeshinAccountCards) return;
        try {
            setProcessing(true);
            const res = await previewHeshinAccountCards(caseId, prmFile, datFile, token, reverseName);
            setPreviewData({
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
                entries: res.entries ?? [],
                problematicCards: res.problematicCards ?? [],
            });
            setPage(0);
        } catch (err) {
            console.error('Heshin account cards preview failed:', err);
            setPreviewData({
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err.message || 'Preview failed'],
                entries: [],
                problematicCards: [],
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleImport = async () => {
        if (!prmFile || !datFile || !caseId || !token || !importHeshinAccountCards) return;
        try {
            setProcessing(true);
            const res = await importHeshinAccountCards(caseId, prmFile, datFile, token, reverseName);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            if (errors > 0) {
                toast.warning(t('ImportSuccessful') + ` – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful'));
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Heshin account cards import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setPrmFile(null);
        setDatFile(null);
        setPreviewData(null);
        setPage(0);
        setRowsPerPage(10);
        onClose();
    };

    if (!open) return null;

    const entries = previewData?.entries ?? [];
    const paginatedEntries = entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{
                        position: 'relative',
                        maxWidth: previewData ? '900px' : '560px',
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('UploadHeshinFiles')} – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <div>
                            <h5>heshin.prm</h5>
                            <DropzoneArea key={prmFile ? prmFile.name : 'prm'} onDrop={handlePrmDrop} accept=".prm" />
                            {prmFile && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {prmFile.name}
                                </Typography>
                            )}
                        </div>
                        <div>
                            <h5>heshin.dat</h5>
                            <DropzoneArea key={datFile ? datFile.name : 'dat'} onDrop={handleDatDrop} accept=".dat" />
                            {datFile && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {datFile.name}
                                </Typography>
                            )}
                        </div>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={reverseName}
                                    onChange={(e) => setReverseName(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={t('HeshinReverseName') || 'Reverse name (name in file is stored RTL)'}
                        />

                        <Box display="flex" gap={1.5} mt={1}>
                            <Button variant="contained" onClick={handlePreview} disabled={!prmFile || !datFile || processing}>
                                {processing && !previewData ? t('Processing') : t('Preview')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleImport}
                                disabled={!previewData || processing || (previewData?.validCount ?? 0) === 0}
                            >
                                {processing && previewData ? t('Processing') : t('Import')}
                            </Button>
                        </Box>
                    </Box>

                    {previewData && (
                        <Box mt={3} sx={{ maxHeight: 520, overflow: 'auto' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('TotalLines') || 'Total lines'}: <strong>{previewData.totalLines}</strong>
                                {' | '}
                                {t('ValidCount') || 'To add'}: <strong>{previewData.validCount}</strong>
                                {' | '}
                                {t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong>
                                {previewData.problematicCards?.length > 0 && (
                                    <> | {t('Duplicates') || 'Duplicates'}: <strong>{previewData.problematicCards.length}</strong></>
                                )}
                            </Typography>

                            {previewData.errorMessages?.length > 0 && (
                                <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 160, overflow: 'auto' }}>
                                    <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                        {t('Errors')} ({previewData.errorMessages.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {previewData.errorMessages.map((msg, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {msg}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {entries.length > 0 && (
                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line') || 'Line'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CardId') || 'Card ID'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Name')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Phone') || 'Phone'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Location') || 'Location'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('AccountCaseId') || 'Account'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Email')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('PalCode') || 'PalCode'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedEntries.map((row, i) => (
                                                <TableRow key={row.lineNumber ?? i}>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.cardId ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.name ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.phoneNumber ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.location ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.accountCaseId ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.email ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.palCode ?? '—'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={entries.length}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                    />
                                </TableContainer>
                            )}

                            {previewData.problematicCards?.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('Duplicates') || 'Duplicates'} ({previewData.problematicCards.length}) – {t('WillNotBeImported') || 'will not be imported'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {previewData.problematicCards.map((pc) => `${t('Line')} ${pc.lineNumber}: ${pc.dto?.cardId ?? pc.dto?.name ?? '—'}`).join(', ')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HeshinAccountCardsImportModal;
